// Considered: the authed app shell in App.tsx (header + outlet) — rejected because the authed
// shell is structurally bound to initialization.userId + a Descope session + impersonation; there is no
// unauthenticated layout. See spec EAINT-663 Design System Reuse Audit.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'localization';
import { Routes, Route, useNavigate, useParams } from 'react-router-dom';
import {
  publicMarketplaceApi,
  PublicDiscoverListing,
  PublicChannelDetail,
  PublicPublisherDetail,
  PublicTemplateDetail,
} from '../../../api/publicMarketplaceApi';
import type { ListChannelsResponse } from '../../../api/marketplaceApi';
import type { Publisher } from 'types/publisher';
import type { LocalizedString } from 'types/context';
import MarketplaceHomeOrganism, { MarketplacePublicDataSource } from './MarketplaceHomeOrganism';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import './PublicMarketplaceShell.scss';

type ListedChannel = ListChannelsResponse['channels'][number];

function toLocalizedString(value: unknown): LocalizedString {
  if (typeof value === 'string') return { default: value };
  if (value !== null && typeof value === 'object' && 'default' in value) {
    return value as LocalizedString;
  }
  return { default: '' };
}

function resolveString(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value !== null && typeof value === 'object' && 'default' in value) {
    return (value as { default: string }).default ?? '';
  }
  return '';
}

function adaptPublicToListedChannel(ch: PublicDiscoverListing): ListedChannel {
  const publisher: Publisher | null = ch.publisher
    ? {
        id: '',
        system_name: ch.publisher.slug,
        slug: ch.publisher.slug,
        display_name: ch.publisher.name,
        display_name_i18n: {},
        bio: null,
        bio_i18n: {},
        locales: [],
        default_locale: '',
        website: null,
        socials: null,
        hue: 0,
        glyph: '',
        glyph_kind: 'letter',
        is_official: false,
        is_featured: false,
        is_archived: false,
        logo_attachment_id: null,
        banner_attachment_id: null,
        status: 'published',
        reviewer_note: null,
        submitted_at: null,
        reviewed_at: null,
        published_at: null,
        reviewed_by: null,
        created_by: '',
        updated_by: '',
        created_at: new Date(0),
        updated_at: new Date(0),
      }
    : null;

  return {
    id: ch.system_name,
    system_name: ch.system_name,
    name: toLocalizedString(ch.name),
    description: toLocalizedString(ch.description),
    owner_sphere_id: '',
    owner_tenant_id: '',
    scope: 'metasphere',
    scope_id: '',
    visibility: 'public',
    status: 'approved',
    is_system: false,
    reviewed_by: null,
    reviewed_at: null,
    rejection_reason: null,
    created_by: '',
    created_at: '',
    updated_at: '',
    glyph: ch.glyph ?? null,
    glyph_kind: ch.glyph_kind === 'letter' ? 'letter' : 'icon',
    tags: ch.tags,
    publisher,
    is_subscribed: false,
    is_mandatory: false,
  };
}

// ─────────────────────────────────────────────────────────────────
// Browse index view (home route)
// ─────────────────────────────────────────────────────────────────

interface PublicBrowseIndexProps {
  onPageViewed?: () => void;
}

const PublicBrowseIndex: React.FC<PublicBrowseIndexProps> = ({ onPageViewed }) => {
  const { t } = useTranslation();

  const [channels, setChannels] = useState<ListedChannel[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fire the page-view callback once on mount.
  useEffect(() => {
    onPageViewed?.();
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    publicMarketplaceApi
      .discover()
      .then((response) => {
        if (cancelled) return;
        setChannels(response.data.map(adaptPublicToListedChannel));
        setTotal(response.total);
      })
      .catch(() => {
        if (cancelled) return;
        setError(t('marketplace.public.loadError'));
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [t]);

  const dataSource: MarketplacePublicDataSource = { channels, total, loading, error };
  return <MarketplaceHomeOrganism dataSource={dataSource} />;
};

// ─────────────────────────────────────────────────────────────────
// Public channel detail view
// ─────────────────────────────────────────────────────────────────

interface PublicDetailSharedProps {
  onSignInForAction?: (returnTo: string, action: 'deploy' | 'fork' | 'subscribe') => void;
  onListingOpened?: (type: 'channel' | 'publisher' | 'template', identifier: string) => void;
}

const PublicChannelDetailView: React.FC<PublicDetailSharedProps> = ({
  onSignInForAction,
  onListingOpened,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { systemName } = useParams<{ systemName: string }>();

  const [channel, setChannel] = useState<PublicChannelDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Hold the analytics callback in a ref so the fetch effect depends only on
  // systemName — an inline callback from the caller must not retrigger the fetch.
  const onListingOpenedRef = useRef(onListingOpened);
  useEffect(() => {
    onListingOpenedRef.current = onListingOpened;
  }, [onListingOpened]);

  useEffect(() => {
    if (!systemName) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    publicMarketplaceApi
      .getChannel(systemName)
      .then((data) => {
        if (cancelled) return;
        setChannel(data);
        onListingOpenedRef.current?.('channel', systemName);
      })
      .catch(() => {
        if (cancelled) return;
        setError(t('marketplace.public.channel.loadError'));
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [systemName, t]);

  const handleBack = useCallback(() => {
    navigate('/marketplace');
  }, [navigate]);

  if (loading) {
    return (
      <div className="public-marketplace-detail">
        <button type="button" className="public-marketplace-detail__back" onClick={handleBack}>
          <i className="pi pi-arrow-left" aria-hidden="true" />
          <span>{t('marketplace.public.backToMarketplace')}</span>
        </button>
        <div className="public-marketplace-detail__loading">
          <span className="marketplace-marketplace__spinner" />
        </div>
      </div>
    );
  }

  if (error || !channel) {
    return (
      <div className="public-marketplace-detail">
        <button type="button" className="public-marketplace-detail__back" onClick={handleBack}>
          <i className="pi pi-arrow-left" aria-hidden="true" />
          <span>{t('marketplace.public.backToMarketplace')}</span>
        </button>
        <p className="public-marketplace-detail__error">
          {error ?? t('marketplace.public.channel.loadError')}
        </p>
      </div>
    );
  }

  const channelName = resolveString(channel.name);
  const channelDesc = resolveString(channel.description);

  return (
    <div className="public-marketplace-detail public-marketplace-channel-detail">
      <button type="button" className="public-marketplace-detail__back" onClick={handleBack}>
        <i className="pi pi-arrow-left" aria-hidden="true" />
        <span>{t('marketplace.public.backToMarketplace')}</span>
      </button>

      <div className="public-marketplace-detail__header">
        <h1 className="public-marketplace-detail__title">{channelName}</h1>
        {channel.publisher && (
          <p className="public-marketplace-detail__meta">
            <span className="public-marketplace-detail__by">{t('marketplace.public.channel.by')}</span>
            <button
              type="button"
              className="public-marketplace-detail__publisher-link"
              onClick={() =>
                navigate(`/marketplace/publishers/${encodeURIComponent(channel.publisher!.slug)}`)
              }
            >
              {channel.publisher.name}
            </button>
          </p>
        )}
        {channelDesc && (
          <p className="public-marketplace-detail__description">{channelDesc}</p>
        )}
        {channel.tags.length > 0 && (
          <div className="public-marketplace-detail__tags">
            {channel.tags.map((tag) => (
              <span key={tag} className="public-marketplace-detail__tag">
                {tag}
              </span>
            ))}
          </div>
        )}
        <ButtonAtom
          label={t('marketplace.public.signInToSubscribe')}
          variant="primary"
          onClick={() =>
            onSignInForAction?.(
              `/marketplace/channels/${encodeURIComponent(systemName!)}`,
              'subscribe'
            )
          }
          className="public-marketplace-channel-detail__subscribe-btn"
        />
      </div>

      <div className="public-marketplace-channel-detail__templates">
        <h2 className="public-marketplace-detail__section-title">
          {t('marketplace.public.channel.templates')}
        </h2>
        {channel.listed_templates.length === 0 ? (
          <p className="public-marketplace-detail__empty">
            {t('marketplace.public.channel.noTemplates')}
          </p>
        ) : (
          <div className="public-marketplace-channel-detail__templates-grid">
            {channel.listed_templates.map((tmpl) => (
              <article
                key={tmpl.system_name}
                className="public-marketplace-channel-detail__template-card"
              >
                <span className="public-marketplace-channel-detail__template-type">
                  {tmpl.type}
                </span>
                <p className="public-marketplace-channel-detail__template-name">{tmpl.name}</p>
                {tmpl.description && (
                  <p className="public-marketplace-channel-detail__template-desc">
                    {tmpl.description}
                  </p>
                )}
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────
// Public publisher detail view
// ─────────────────────────────────────────────────────────────────

const PublicPublisherDetailView: React.FC<PublicDetailSharedProps> = ({
  onListingOpened,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();

  const [publisher, setPublisher] = useState<PublicPublisherDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Hold the analytics callback in a ref so the fetch effect depends only on
  // slug — an inline callback from the caller must not retrigger the fetch.
  const onListingOpenedRef = useRef(onListingOpened);
  useEffect(() => {
    onListingOpenedRef.current = onListingOpened;
  }, [onListingOpened]);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    publicMarketplaceApi
      .getPublisher(slug)
      .then((data) => {
        if (cancelled) return;
        setPublisher(data);
        onListingOpenedRef.current?.('publisher', slug);
      })
      .catch(() => {
        if (cancelled) return;
        setError(t('marketplace.public.publisher.loadError'));
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, t]);

  const handleBack = useCallback(() => {
    navigate('/marketplace');
  }, [navigate]);

  if (loading) {
    return (
      <div className="public-marketplace-detail">
        <button type="button" className="public-marketplace-detail__back" onClick={handleBack}>
          <i className="pi pi-arrow-left" aria-hidden="true" />
          <span>{t('marketplace.public.backToMarketplace')}</span>
        </button>
        <div className="public-marketplace-detail__loading">
          <span className="marketplace-marketplace__spinner" />
        </div>
      </div>
    );
  }

  if (error || !publisher) {
    return (
      <div className="public-marketplace-detail">
        <button type="button" className="public-marketplace-detail__back" onClick={handleBack}>
          <i className="pi pi-arrow-left" aria-hidden="true" />
          <span>{t('marketplace.public.backToMarketplace')}</span>
        </button>
        <p className="public-marketplace-detail__error">
          {error ?? t('marketplace.public.publisher.loadError')}
        </p>
      </div>
    );
  }

  return (
    <div className="public-marketplace-detail public-marketplace-publisher-detail">
      <button type="button" className="public-marketplace-detail__back" onClick={handleBack}>
        <i className="pi pi-arrow-left" aria-hidden="true" />
        <span>{t('marketplace.public.backToMarketplace')}</span>
      </button>

      <div className="public-marketplace-detail__header">
        <h1 className="public-marketplace-detail__title">{publisher.name}</h1>
        {publisher.is_official && (
          <span className="public-marketplace-publisher-detail__official-badge">
            <i className="pi pi-verified" aria-hidden="true" />
            {t('marketplace.public.publisher.official')}
          </span>
        )}
        {publisher.bio && (
          <p className="public-marketplace-detail__description">{publisher.bio}</p>
        )}
      </div>

      <div className="public-marketplace-publisher-detail__channels">
        <h2 className="public-marketplace-detail__section-title">
          {t('marketplace.public.publisher.channels')}
        </h2>
        {publisher.channels.length === 0 ? (
          <p className="public-marketplace-detail__empty">
            {t('marketplace.public.publisher.noChannels')}
          </p>
        ) : (
          <div className="public-marketplace-publisher-detail__channels-list">
            {publisher.channels.map((ch) => (
              <button
                key={ch.system_name}
                type="button"
                className="public-marketplace-publisher-detail__channel-card"
                onClick={() =>
                  navigate(`/marketplace/channels/${encodeURIComponent(ch.system_name)}`)
                }
              >
                <span className="public-marketplace-publisher-detail__channel-name">
                  {resolveString(ch.name)}
                </span>
                <i className="pi pi-arrow-right" aria-hidden="true" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────
// Public template detail view
// ─────────────────────────────────────────────────────────────────

const PublicTemplateDetailView: React.FC<PublicDetailSharedProps> = ({
  onSignInForAction,
  onListingOpened,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { systemName } = useParams<{ systemName: string }>();

  const [template, setTemplate] = useState<PublicTemplateDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Hold the analytics callback in a ref so the fetch effect depends only on
  // systemName — an inline callback from the caller must not retrigger the fetch.
  const onListingOpenedRef = useRef(onListingOpened);
  useEffect(() => {
    onListingOpenedRef.current = onListingOpened;
  }, [onListingOpened]);

  useEffect(() => {
    if (!systemName) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    publicMarketplaceApi
      .getTemplate(systemName)
      .then((data) => {
        if (cancelled) return;
        setTemplate(data);
        onListingOpenedRef.current?.('template', systemName);
      })
      .catch(() => {
        if (cancelled) return;
        setError(t('marketplace.public.template.loadError'));
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [systemName, t]);

  const handleBack = useCallback(() => {
    navigate('/marketplace');
  }, [navigate]);

  if (loading) {
    return (
      <div className="public-marketplace-detail">
        <button type="button" className="public-marketplace-detail__back" onClick={handleBack}>
          <i className="pi pi-arrow-left" aria-hidden="true" />
          <span>{t('marketplace.public.backToMarketplace')}</span>
        </button>
        <div className="public-marketplace-detail__loading">
          <span className="marketplace-marketplace__spinner" />
        </div>
      </div>
    );
  }

  if (error || !template) {
    return (
      <div className="public-marketplace-detail">
        <button type="button" className="public-marketplace-detail__back" onClick={handleBack}>
          <i className="pi pi-arrow-left" aria-hidden="true" />
          <span>{t('marketplace.public.backToMarketplace')}</span>
        </button>
        <p className="public-marketplace-detail__error">
          {error ?? t('marketplace.public.template.loadError')}
        </p>
      </div>
    );
  }

  return (
    <div className="public-marketplace-detail public-marketplace-template-detail">
      <button type="button" className="public-marketplace-detail__back" onClick={handleBack}>
        <i className="pi pi-arrow-left" aria-hidden="true" />
        <span>{t('marketplace.public.backToMarketplace')}</span>
      </button>

      <div className="public-marketplace-detail__header">
        <span className="public-marketplace-template-detail__type-badge">{template.type}</span>
        <h1 className="public-marketplace-detail__title">{template.name}</h1>
        {template.publisher && (
          <p className="public-marketplace-detail__meta">
            {t('marketplace.public.template.publishedBy', {
              publisher: template.publisher.name ?? template.publisher.slug,
            })}
          </p>
        )}
        {template.description && (
          <p className="public-marketplace-detail__description">{template.description}</p>
        )}
        {template.tags.length > 0 && (
          <div className="public-marketplace-detail__tags">
            {template.tags.map((tag) => (
              <span key={tag} className="public-marketplace-detail__tag">
                {tag}
              </span>
            ))}
          </div>
        )}
        <ButtonAtom
          label={t('marketplace.public.signInToDeploy')}
          variant="primary"
          onClick={() =>
            onSignInForAction?.(
              `/marketplace/templates/${encodeURIComponent(systemName!)}`,
              'deploy'
            )
          }
          className="public-marketplace-template-detail__deploy-btn"
        />
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────
// Shell — main export
// ─────────────────────────────────────────────────────────────────

export interface PublicMarketplaceShellProps {
  /** Called on mount of the browse grid for analytics. */
  onPageViewed?: () => void;
  /** Called when the sign-in CTA is clicked anywhere in the public surface (no returnTo). */
  onSignIn?: () => void;
  /** Called when a deploy/subscribe/fork CTA is clicked on a detail page — includes the resource deep link. */
  onSignInForAction?: (returnTo: string, action: 'deploy' | 'fork' | 'subscribe') => void;
  /** Called when a detail page loads — for `public_listing_opened` analytics. */
  onListingOpened?: (type: 'channel' | 'publisher' | 'template', identifier: string) => void;
}

const PublicMarketplaceShellOrganism: React.FC<PublicMarketplaceShellProps> = ({
  onPageViewed,
  onSignIn,
  onSignInForAction,
  onListingOpened,
}) => {
  const { t } = useTranslation();

  const handleSignIn = useCallback(() => {
    onSignIn?.();
  }, [onSignIn]);

  return (
    <div className="public-marketplace-shell" data-testid="public-marketplace-shell">
      <div className="public-marketplace-shell__header">
        <span className="public-marketplace-shell__header-tagline">
          {t('marketplace.public.headerTagline')}
        </span>
        <ButtonAtom
          label={t('marketplace.public.signIn')}
          variant="primary"
          onClick={handleSignIn}
          className="public-marketplace-shell__sign-in-btn"
        />
      </div>
      <div className="public-marketplace-shell__content">
        <Routes>
          <Route
            index
            element={<PublicBrowseIndex onPageViewed={onPageViewed} />}
          />
          <Route
            path="channels/:systemName"
            element={
              <PublicChannelDetailView
                onSignInForAction={onSignInForAction}
                onListingOpened={onListingOpened}
              />
            }
          />
          <Route
            path="publishers/:slug"
            element={
              <PublicPublisherDetailView
                onListingOpened={onListingOpened}
              />
            }
          />
          <Route
            path="templates/:systemName"
            element={
              <PublicTemplateDetailView
                onSignInForAction={onSignInForAction}
                onListingOpened={onListingOpened}
              />
            }
          />
          <Route
            path="*"
            element={<PublicBrowseIndex onPageViewed={onPageViewed} />}
          />
        </Routes>
      </div>
    </div>
  );
};

PublicMarketplaceShellOrganism.displayName = 'PublicMarketplaceShell';

export default PublicMarketplaceShellOrganism;
